import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { datasets, marketData } from '../api/client';

const CATEGORY_LABELS = {
  forex: 'Forex',
  indices: 'Indices',
  crypto: 'Crypto',
  commodities: 'Commodities',
};

function DownloadProgress({ taskId, onDone }) {
  const [task, setTask] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!taskId) return;
    const poll = () => {
      marketData.getTask(taskId).then(res => {
        setTask(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(intervalRef.current);
          if (onDone) onDone(res.data);
        }
      }).catch(() => {});
    };
    poll();
    intervalRef.current = setInterval(poll, 2000);
    return () => clearInterval(intervalRef.current);
  }, [taskId, onDone]);

  if (!task) return null;

  return (
    <div className="onb-download-progress">
      <div className="onb-progress-info">
        <span className="onb-progress-symbol">{task.symbol}</span>
        <span className="onb-progress-tf">{task.timeframe}</span>
        <span className={`onb-progress-status onb-status-${task.status}`}>
          {task.status === 'pending' ? 'En attente...' :
           task.status === 'running' ? `${task.progress}%` :
           task.status === 'completed' ? 'Terminé !' : 'Erreur'}
        </span>
      </div>
      {(task.status === 'running' || task.status === 'pending') && (
        <div className="onb-progress-bar">
          <div className="onb-progress-fill" style={{ width: `${task.progress}%` }} />
        </div>
      )}
      {task.status === 'completed' && task.dataset_id && (
        <Link to={`/session/new/${task.dataset_id}`} className="btn btn-sm" style={{ marginTop: 6 }}>
          Lancer le Replay
        </Link>
      )}
      {task.status === 'failed' && (
        <div className="onb-progress-error">{task.error_message}</div>
      )}
    </div>
  );
}

function OnboardingView({ onDatasetCreated }) {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [downloading, setDownloading] = useState({});  // symbol -> taskId
  const [activeTasks, setActiveTasks] = useState([]);

  useEffect(() => {
    marketData.popular()
      .then(res => setInstruments(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (inst) => {
    if (downloading[inst.symbol]) return;

    const now = new Date();
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(now.getMonth() - 3);

    setDownloading(prev => ({ ...prev, [inst.symbol]: 'starting' }));
    try {
      const res = await marketData.startDownload({
        source: inst.source,
        symbol: inst.symbol,
        timeframe: inst.default_timeframe,
        date_from: threeMonthsAgo.toISOString().split('T')[0],
        date_to: now.toISOString().split('T')[0],
      });
      setDownloading(prev => ({ ...prev, [inst.symbol]: res.data.task_id }));
      setActiveTasks(prev => [...prev, { taskId: res.data.task_id, symbol: inst.symbol }]);
    } catch (err) {
      setDownloading(prev => ({ ...prev, [inst.symbol]: null }));
      alert(err.response?.data?.error || 'Download failed');
    }
  };

  const handleDone = useCallback((task) => {
    if (task.status === 'completed' && onDatasetCreated) {
      onDatasetCreated();
    }
  }, [onDatasetCreated]);

  const categories = ['all', ...new Set(instruments.map(i => i.category))];
  const filtered = activeCategory === 'all'
    ? instruments
    : instruments.filter(i => i.category === activeCategory);

  if (loading) return <div className="onb-loading">Chargement des instruments...</div>;

  return (
    <div className="onboarding">
      <div className="onb-hero">
        <h1>Bienvenue sur TradeSimul</h1>
        <p>Pour commencer, télécharge les données d'un instrument. Choisis parmi les plus populaires ci-dessous, le download se fait en 1 clic !</p>
      </div>

      {/* Active downloads */}
      {activeTasks.length > 0 && (
        <div className="onb-active-downloads">
          <h3>Téléchargements en cours</h3>
          {activeTasks.map(t => (
            <DownloadProgress key={t.taskId} taskId={t.taskId} onDone={handleDone} />
          ))}
        </div>
      )}

      {/* Category filter */}
      <div className="onb-categories">
        {categories.map(cat => (
          <button
            key={cat}
            className={`onb-cat-btn ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat === 'all' ? 'Tous' : CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* Instrument cards */}
      <div className="onb-grid">
        {filtered.map(inst => {
          const dl = downloading[inst.symbol];
          const isDownloading = dl && dl !== 'starting' && dl !== null;
          const isStarting = dl === 'starting';

          return (
            <div key={inst.symbol} className="onb-card">
              <div className="onb-card-header">
                <span className="onb-card-emoji">{inst.emoji}</span>
                <div>
                  <div className="onb-card-name">{inst.name}</div>
                  <div className="onb-card-symbol">{inst.symbol.toUpperCase()}</div>
                </div>
              </div>
              <div className="onb-card-desc">{inst.description}</div>
              <div className="onb-card-meta">
                <span className="onb-card-badge">{inst.source}</span>
                <span className="onb-card-badge">{inst.default_timeframe}</span>
                <span className="onb-card-badge">3 mois</span>
              </div>
              <button
                className={`onb-download-btn ${isDownloading ? 'downloading' : ''}`}
                onClick={() => handleDownload(inst)}
                disabled={!!dl}
              >
                {isStarting ? 'Lancement...' :
                 isDownloading ? 'En cours...' :
                 'Télécharger'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="onb-footer">
        <p>Tu cherches un autre instrument ?</p>
        <Link to="/market-data" className="btn">Recherche avancée</Link>
      </div>
    </div>
  );
}

export default function DatasetsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDatasets = async () => {
    try {
      const res = await datasets.list();
      setList(res.data);
    } catch (err) {
      setError('Failed to load datasets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dataset and all its candles?')) return;
    try {
      await datasets.delete(id);
      setList(list.filter((d) => d.id !== id));
    } catch {
      setError('Failed to delete dataset.');
    }
  };

  if (loading) return <div className="page">Loading...</div>;

  // Show onboarding if no datasets
  if (list.length === 0 && !error) {
    return (
      <div className="page">
        <OnboardingView onDatasetCreated={fetchDatasets} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>My Datasets</h2>
        <Link to="/upload" className="btn">
          + Import Data
        </Link>
      </div>
      {error && <div className="error-msg">{error}</div>}
      {list.length === 0 ? (
        <div className="empty-state">
          <p>No datasets yet.</p>
          <Link to="/upload" className="btn">
            Import your first dataset
          </Link>
        </div>
      ) : (
        <div className="dataset-grid">
          {list.map((ds) => (
            <div key={ds.id} className="card dataset-card">
              <h3>{ds.name}</h3>
              <div className="dataset-meta">
                <span className="badge">{ds.instrument}</span>
                <span className="badge">{ds.timeframe}</span>
                <span>{ds.bar_count.toLocaleString()} bars</span>
              </div>
              <div className="dataset-dates">
                {new Date(ds.date_start).toLocaleDateString()} —{' '}
                {new Date(ds.date_end).toLocaleDateString()}
              </div>
              {ds.warnings?.length > 0 && (
                <div className="dataset-warnings">
                  {ds.warnings.length} warning(s)
                </div>
              )}
              <div className="dataset-actions">
                <Link to={`/session/new/${ds.id}`} className="btn btn-sm">
                  Start Replay
                </Link>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(ds.id)}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
